import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'stepsList', 'step',
    'pipelineType', 'aiModeButton'
  ]
  static values = { pipelineType: String }

  static AI_MODE_BY_TYPE = {
    property_exploration: 'with_ai',
    custom_templates:     'without_ai',
  }

  static TYPES_BY_AI_MODE = {
    with_ai:    [['property_exploration', 'Remarketing Automático']],
    without_ai: [['custom_templates', 'Engajamento Manual']],
  }

  connect() {
    this.applyPipelineType()
  }

  aiModeClicked(event) {
    event?.preventDefault()
    const mode = event.currentTarget.dataset.aiModeValue
    const types = this.constructor.TYPES_BY_AI_MODE[mode] || []
    const nextType = types[0]?.[0]
    if (!nextType) return
    if (nextType === this.pipelineTypeValue) {
      this.syncAiModeButtons(mode)
      return
    }
    this.repopulatePipelineTypeSelect(types, nextType)
    this.updatePipelineType(nextType)
  }

  pipelineTypeChanged() {
    if (!this.hasPipelineTypeTarget) return
    const nextType = this.pipelineTypeTarget.value
    if (!nextType || nextType === this.pipelineTypeValue) return
    this.updatePipelineType(nextType)
  }

  repopulatePipelineTypeSelect(types, selectedValue) {
    if (!this.hasPipelineTypeTarget) return
    this.pipelineTypeTarget.innerHTML = types
      .map(([value, label]) => {
        const sel = value === selectedValue ? ' selected' : ''
        return `<option value="${value}"${sel}>${this.escape(label)}</option>`
      })
      .join('')
  }

  updatePipelineType(nextType) {
    this.pipelineTypeValue = nextType
    if (this.hasPipelineTypeTarget && this.pipelineTypeTarget.value !== nextType) {
      this.pipelineTypeTarget.value = nextType
    }
    this.applyPipelineType()
    this.refreshFirstStep()
  }

  applyPipelineType() {
    const type = this.pipelineTypeValue
    const mode = this.constructor.AI_MODE_BY_TYPE[type]

    this.syncAiModeButtons(mode)
  }

  syncAiModeButtons(mode) {
    if (!this.hasAiModeButtonTarget) return
    this.aiModeButtonTargets.forEach((btn) => {
      const selected = btn.dataset.aiModeValue === mode
      btn.classList.toggle('bg-white', selected)
      btn.classList.toggle('text-gray-900', selected)
      btn.classList.toggle('shadow-sm', selected)
      btn.classList.toggle('border', selected)
      btn.classList.toggle('border-gray-200', selected)
      btn.classList.toggle('text-gray-600', !selected)
      btn.classList.toggle('hover:text-gray-900', !selected)
      btn.classList.toggle('cursor-pointer', !selected)
    })
  }

  refreshFirstStep() {
    const firstStep = this.stepTargets[0]
    if (!firstStep) return
    const type = this.pipelineTypeValue
    firstStep.outerHTML = this.buildStepHTML(0, { lockedType: this.lockedTypeFor(type) })
  }

  lockedTypeFor(type) {
    return type === 'property_exploration' ? type : null
  }

  addStep(event) {
    event?.preventDefault()
    const nextIndex = this.stepTargets.length
    this.stepsListTarget.insertAdjacentHTML('beforeend', this.buildStepHTML(nextIndex, { lockedType: null }))
    this.renumberSteps()
  }

  removeStep(event) {
    event?.preventDefault()
    const stepEl = event.currentTarget.closest('[data-engagement-pipeline-builder-target="step"]')
    if (!stepEl) return
    if (stepEl.dataset.lockedFirstStep === 'true') {
      alert('A primeira etapa deste tipo de régua não pode ser removida.')
      return
    }
    const visibleSteps = this.stepTargets.filter(el => el.dataset.markedForDestruction !== 'true')
    if (visibleSteps.length <= 1) {
      alert('Mantenha ao menos uma etapa na régua.')
      return
    }

    const idInput = stepEl.querySelector('input[name$="[id]"]')
    if (idInput) {
      // Persisted step: keep the inputs in the form, add _destroy=1 so
      // accepts_nested_attributes_for deletes the row on save.
      stepEl.dataset.markedForDestruction = 'true'
      stepEl.classList.add('hidden')
      const destroyInput = document.createElement('input')
      destroyInput.type = 'hidden'
      destroyInput.name = idInput.name.replace(/\[id\]$/, '[_destroy]')
      destroyInput.value = '1'
      stepEl.appendChild(destroyInput)
    } else {
      stepEl.remove()
    }
    this.renumberSteps()
  }

  renumberSteps() {
    let visiblePosition = 0
    this.stepTargets.forEach((stepEl, index) => {
      stepEl.dataset.stepIndex = index
      if (stepEl.dataset.markedForDestruction === 'true') return
      visiblePosition += 1
      const badge = stepEl.querySelector('.step-badge')
      if (badge) badge.textContent = String(visiblePosition)
    })
  }

  templateConfiguratorHtml(index) {
    return `
      <div class="space-y-3"
        data-controller="engagement-pipeline-template"
        data-engagement-pipeline-template-step-index-value="${index}"
        data-engagement-pipeline-step-target="templateSection">
        <div>
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Template</label>
          <select name="pipeline[steps_attributes][${index}][template_meta_id]"
            class="select select-bordered w-full bg-white h-10 min-h-10"
            data-engagement-pipeline-template-target="select"
            data-action="change->engagement-pipeline-template#templateChanged">
            <option value="">Escolha um template...</option>
            ${this.templateOptionsHtml()}
          </select>
        </div>
        <div class="hidden" data-engagement-pipeline-template-target="previewWrapper">
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Pré-visualização</label>
          <div class="p-3 bg-white border border-gray-200 rounded text-sm text-gray-700 whitespace-pre-line leading-relaxed"
            data-engagement-pipeline-template-target="preview"></div>
        </div>
        <div class="hidden space-y-2" data-engagement-pipeline-template-target="variablesWrapper">
          <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Variáveis</label>
          <div data-engagement-pipeline-template-target="variablesList"></div>
        </div>
      </div>
    `
  }

  templateOptionsHtml() {
    const node = document.getElementById('engagement-pipeline-templates')
    if (!node) return ''
    try {
      const registry = JSON.parse(node.textContent || '{}')
      return Object.entries(registry)
        .map(([id, data]) => `<option value="${id}">${this.escape(data.label)}</option>`)
        .join('')
    } catch (e) {
      return ''
    }
  }

  escape(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  }

  buildStepHTML(index, { lockedType = null } = {}) {
    const isAiInitial = lockedType === 'property_exploration' && index === 0
    const locked = isAiInitial
    const contentSelectable = this.pipelineTypeValue === 'property_exploration'

    const cardClasses = isAiInitial
      ? 'bg-amber-50/40 border-amber-200'
      : 'bg-gray-50 border-gray-200'

    const badge = isAiInitial
      ? `<div class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 border border-amber-300 rounded-full text-xs font-semibold text-amber-800">
           <span class="inline-block w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
           Início Remarketing Automático
         </div>`
      : `<div class="flex items-center justify-center w-8 h-8 bg-gray-900 text-white text-sm font-semibold rounded-full step-badge">${index + 1}</div>`

    const removeBtn = locked
      ? ''
      : `<button type="button" class="text-gray-400 hover:text-red-600 cursor-pointer text-2xl leading-none"
           data-action="engagement-pipeline-builder#removeStep" title="Remover etapa">×</button>`

    const contentTypeSelector = contentSelectable ? this.contentTypeSelectorHtml(index) : ''
    const mainContent = this.templateConfiguratorHtml(index)
    const recommendationsSection = this.dynamicRecommendationsSectionHtml()
    const optInSection = this.optInSectionHtml()

    const delayBlock = index === 0
      ? ''
      : `<div>
           <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Quando disparar</label>
           <div class="flex items-center gap-2">
             <input type="number" min="0" value="3" name="pipeline[steps_attributes][${index}][delay_value]" class="input input-bordered w-20 bg-white h-10 min-h-10" />
             <select name="pipeline[steps_attributes][${index}][delay_unit]" class="select select-bordered w-28 bg-white h-10 min-h-10">
               <option value="minutes">minutos</option>
               <option value="hours">horas</option>
               <option value="days" selected>dias</option>
             </select>
             <span class="text-sm text-gray-600 whitespace-nowrap">após etapa anterior</span>
           </div>
         </div>`

    return `
      <section class="${cardClasses} border rounded-lg p-5 transition"
        data-controller="engagement-pipeline-step"
        data-engagement-pipeline-step-content-type-value="template"
        data-engagement-pipeline-builder-target="step" data-step-index="${index}"
        data-locked-first-step="${locked}" data-locked-first-step-type="${locked ? lockedType : ''}">
        <div class="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
          <div class="flex items-center gap-3">
            ${badge}
            <input type="text" placeholder="Nome da etapa"
              class="text-base font-semibold text-gray-900 bg-transparent border-0 px-0 focus:outline-none focus:ring-0 w-72" />
          </div>
          ${removeBtn}
        </div>
        <div class="space-y-5">
          ${contentTypeSelector}
          ${mainContent}
          ${recommendationsSection}
          ${optInSection}
          ${delayBlock}
        </div>
      </section>
    `
  }

  contentTypeSelectorHtml(index) {
    return `
      <div>
        <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Conteúdo</label>
        <div class="inline-flex items-center h-10 bg-gray-100 border border-gray-200 rounded-lg p-0.5 gap-0.5">
          <button type="button"
            class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition bg-white text-gray-900 shadow-sm border border-gray-200"
            data-engagement-pipeline-step-target="contentTypeButton"
            data-content-type-value="template"
            data-action="click->engagement-pipeline-step#contentTypeClicked">Template</button>
          <button type="button"
            class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition text-gray-600 hover:text-gray-900 cursor-pointer"
            data-engagement-pipeline-step-target="contentTypeButton"
            data-content-type-value="dynamic_recommendations"
            data-action="click->engagement-pipeline-step#contentTypeClicked">Recomendações Dinâmicas</button>
          <button type="button"
            class="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition text-gray-600 hover:text-gray-900 cursor-pointer"
            data-engagement-pipeline-step-target="contentTypeButton"
            data-content-type-value="opt_in"
            data-action="click->engagement-pipeline-step#contentTypeClicked">Check-in da busca</button>
        </div>
        <input type="hidden" name="pipeline[steps_attributes][${index}][content_type]" value="template"
          data-engagement-pipeline-step-target="contentTypeInput" />
      </div>
    `
  }

  dynamicRecommendationsSectionHtml() {
    return `
      <div class="hidden p-4 bg-white border border-violet-200 rounded"
        data-engagement-pipeline-step-target="dynamicRecommendationsSection">
        <div class="flex items-start gap-3">
          <div class="shrink-0 w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
            <span class="text-violet-600 text-base">⌂</span>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900 mb-1">Sugestões automáticas de imóveis</div>
            <p class="text-xs text-gray-600">A IA busca imóveis que combinam com o perfil do cliente (baseado no lead e nas conversas anteriores) e envia como sugestão. Nenhum template é necessário.</p>
          </div>
        </div>
      </div>
    `
  }

  optInSectionHtml() {
    return `
      <div class="hidden p-4 bg-white border border-emerald-200 rounded"
        data-engagement-pipeline-step-target="optInSection">
        <div class="flex items-start gap-3">
          <div class="shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <span class="text-emerald-600 text-base">✋</span>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900 mb-1">Check-in da busca com botões</div>
            <p class="text-xs text-gray-600">Pergunta ao cliente se ele encontrou o que procurava ("status da busca"), com botões para retomar ou encerrar. Quem ainda busca segue conversando com a IA; quem já encontrou nunca mais entra em réguas de engajamento. A régua aguarda a resposta por até 7 dias. Nenhum template precisa ser selecionado.</p>
          </div>
        </div>
      </div>
    `
  }
}
